import Button from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import { Box, Inline, Stack, xcss } from '@atlaskit/primitives';
import SectionMessage from '@atlaskit/section-message';

import RovoIcon from '../../../assets/Apps-light_Rovo_brand_RGB.svg';

const containerStyles = xcss({
  padding: 'space.200',
  maxWidth: '1440px',
  margin: '0 auto',
});

const headerStyles = xcss({
  textAlign: 'center',
  padding: 'space.200',
});

const inlineStyles = xcss({
  alignItems: 'center',
  justifyContent: 'center',
});

const boxStyles = xcss({
    padding: 'space.200',
});

const buttonContainerStyles = xcss({
    display: 'flex',
    justifyContent: 'center',
});

export default function SlackAppLanding() {
  return (
    <Box xcss={containerStyles}>
      <Stack space="space.400">
        {/* Header */}
        <Box xcss={headerStyles}>
          <Inline xcss={inlineStyles}>
            <RovoIcon  />
          </Inline>
          <Box xcss={boxStyles}>
            <Heading size='small'>AI-powered search and assistance for Slack—work faster and smarter.</Heading>
          </Box>
        </Box>

        {/* Main Description */}
        <Box style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
          <Box style={{ width: '50%', fontSize: '16px', fontWeight: '500'}}>
            <Stack space="space.200">
              <p>
                {`Atlassian Rovo is your intelligent AI assistant for real-time search, knowledge discovery, and 
                  seamless collaboration. Rovo brings together the power of Atlassian's AI with Slack's communication, 
                  enabling you to find answers, summarize threads, and automate support without leaving your chat.`}
              </p>
              <p>
                {`Whether you need to search across Atlassian and external tools, get instant help via @mentions, or 
                  deploy custom agents for specialized workflows, Rovo is designed to make your team faster and 
                  smarter. The Atlassian Rovo app enables you to have private DMs, right-rail AI assistance, and 
                  flexible agent configurations in your Slack channels.`}
              </p>
              <p>
                {`Once you've installed the Rovo app, Type @Rovo in a channel you'd like to invite Rovo to, then you can 
                  use the /rovo add command to finalize setup.`}
              </p>
              <p>
                You can also bring Slack to Rovo and <a href="https://support.atlassian.com/organization-administration/docs/connect-slack-to-rovo/" target="_blank" rel="noopener noreferrer">connect it</a> to 
                find your conversations where you work. That means you can search your channels and messages from within your 
                Atlassian apps to find key conversations. This requires an organization admin sets up the Slack connector in <a href="https://support.atlassian.com/organization-administration/docs/explore-an-atlassian-organization/" target="_blank" rel="noopener noreferrer">Atlassian Administration</a>
              </p>
              <p>
                See more about <a href="https://www.atlassian.com/trust/privacy/how-we-handle-your-data" target="_blank" rel="noopener noreferrer">how Atlassian handles data privacy.</a>
              </p>
              <Box xcss={buttonContainerStyles}>
                <Button 
                  appearance="primary" 
                  onClick={() => window.open('/slack/onboard/welcome', '_blank')}
                >
                  Add to Slack
                </Button>
              </Box>
            </Stack>
          </Box>

          {/* Features */}
          <Box>
            <Heading size='medium'>Features</Heading>
            <Stack space="space.200">
              <Box>
                <Heading size='small'>Direct messaging with Rovo</Heading>
                <p>Chat privately with Rovo to get instant answers, search knowledge, or automate tasks.</p>
              </Box>
              <Box>
                <Heading size='small'>AI assistance in the right sidebar</Heading>
                <p>{`Access Rovo's AI in Slack's right sidebar, leveraging channel context for smarter, more relevant help.`}</p>
              </Box>
              <Box>
                <Heading size='small'>@Mention Atlassian Rovo</Heading>
                <p>Mention Rovo in any channel to get immediate, context-aware assistance or thread summaries.</p>
              </Box>
              <Box>
                <Heading size='small'>Configure triggers in different channels</Heading>
                <p>Set up triggers for Rovo or custom agents for specific uses like deflecting questions in help channels.</p>
              </Box>
              <Box>
                <Heading size='small'>Thread summarization</Heading>
                <p>Use the message action or @mention Rovo in a thread to get concise, AI-generated summaries.</p>
              </Box>
              <Box>
                <Heading size='small'>Easy migration from legacy app</Heading>
                <p>Simple steps to migrate from the legacy Rovo app—ensuring a smooth transition for your team.</p>
              </Box>
              <Box>
                <Heading size='small'>Actions</Heading>
                <p>Create and update confluence pages, Jira issues and JSM tickets from Slack.</p>
              </Box>
            </Stack>
          </Box>
        </Box>

        <Box style={{margin: '0 auto', width: '800px'}}>
          <SectionMessage appearance="information">
                <p style={{fontSize: '16px', fontWeight: '400'}}>
                  <a href="https://www.atlassian.com/trust/atlassian-intelligence" target="_blank" rel="noopener noreferrer">Atlassian Rovo uses AI.</a> It can make mistakes so ensure you verify its responses.
                </p>
                <p style={{fontSize: '16px', fontWeight: '400'}}>
                  Note: This app uses the assistant:write scope, which requires a paid Slack plan.
                </p>
          </SectionMessage>
        </Box>

        {/* Configuration */}
        <Box>
          <Heading size='medium'>Configuration</Heading>
          <Stack space="space.300">
            <Box>
              <Heading size='small'>Installation</Heading>
              <ul>
                <li>{`Go to Slack's Apps section and search for Atlassian Rovo.`}</li>
                <li>Add the app to your workspace and follow the steps to connect your Atlassian account (if required).</li>
                <li>Start a DM with Rovo, or invite it to channels for group assistance.</li>
              </ul>
            </Box>
            <Box>
              <Heading size='small'>Permissions</Heading>
              <ul>
                <li>In DMs, Rovo uses your user permissions to provide personalized answers.</li>
                <li>In channels, Rovo uses bot permissions to protect sensitive information.</li>
                <li>Rovo can view messages, respond to mentions, and summarize threads as authorized.</li>
              </ul>
            </Box>
            <Box>
              <Heading size='small'>Set up custom agents</Heading>
              <ul>
                <li>Use <b>/rovo add</b> to select or add custom agents.</li>
                <li>Manage channel settings with <b>/rovo manage</b>.</li>
              </ul>
            </Box>
            <Box>
              <Heading size='small'>Set up Rovo in the right sidebar</Heading>
              <p>From your navigation preferences, add Rovo to the top of Slack for easy access to Rovo assistance in the right sidebar.</p>
            </Box>
          </Stack>
        </Box>

        {/* Security & Compliance */}
        <Box>
          <Heading size='medium'>Security & Compliance</Heading>
          <Stack space="space.300">
            <Box>
              <Heading size='small'>Data privacy</Heading>
              <p>
                Rovo respects your Slack workspace privacy settings and only accesses data as permitted by your 
                installation and user permissions. See our full policy here - <a href="https://www.atlassian.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer">https://www.atlassian.com/legal/privacy-policy</a>
              </p>
            </Box>
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
}
